import React, { useCallback, useContext, useEffect, useState } from "react";
import { Box, Chip, Collapse, LinearProgress, Typography } from "@mui/material";
import { useTextDomainContext } from "@/app/providers";
import { useDropzone } from "react-dropzone";
import type { Accept, FileWithPath } from "react-dropzone";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { Stack } from "@mui/system";
import { object, func, string } from "prop-types";

/**
 * A file dropzone. It will use to select files for upload.
 * @param props
 * @returns {Element}
 * @link https://developer.mozilla.org/en-US/docs/Web/HTTP/Basics_of_HTTP/MIME_types/Common_types Helping link for MIME type
 * @class
 */
type DropzoneProps = {
  accept?: Accept;
  getSelected?: (files: FileWithPath[]) => void;
  value?: string;
};

export const Dropzone = (props: DropzoneProps) => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const propsDefault = {
    accept: {
      "image/png": [".png"],
      "image/jpeg": [".jpeg", ".jpg"],
      "text/csv": [".csv"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
    },
  };

  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileWithPath[]>([]);
  const [highlighted, setHighlighted] = useState(false);
  const [accept, setAccept] = useState(props.accept || propsDefault.accept);

  const onDrop = useCallback(
    <T extends File>(acceptedFiles: T[]) => {
      const files = acceptedFiles as unknown as FileWithPath[];
      setSelectedFiles([...selectedFiles, ...files]);
    },
    [selectedFiles],
  );

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: accept,
  });

  useEffect(() => {
    if (typeof props.accept !== "undefined") {
      setAccept(props.accept);
    }
  }, [props.accept]);

  const { getSelected } = props;
  useEffect(() => {
    if (typeof getSelected === "function") {
      getSelected(selectedFiles);
    }
  }, [getSelected, selectedFiles]);
  // useEffect(() => {
  //   if (typeof props.getSelected === "function") {
  //     props.getSelected(selectedFiles);
  //   }
  // }, [selectedFiles]);

  const LinearProgressWithLabel = (
    props: React.ComponentProps<typeof LinearProgress>,
  ) => {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            mr: 1,
          }}
        >
          <LinearProgress {...props} />
        </Box>
        {props.value && (
          <Box
            sx={{
              minWidth: 35,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary" }}
            >{`${Math.round(props.value)}%`}</Typography>
          </Box>
        )}
      </Box>
    );
  };

  const handleDelete = (file: FileWithPath) => {
    setSelectedFiles(selectedFiles.filter((fileObj) => fileObj !== file));
    //TODO:  Following two lines is only for removing warnings. As soon as we will add it in other side of the code, we can remove them
    setLoading(false);
    setProgress(null);
  };

  return (
    <>
      <Box
        sx={{
          p: 1,
          backgroundColor: "secondary.50",
          border: "1px solid",
          minHeight: "52px",
          borderColor: "secondary.200",
          borderRadius: "8px",
          cursor: "pointer",
        }}
        {...getRootProps({
          className: `p-16 my-10 border text-center ${
            highlighted ? "border-primary" : "border-secondary "
          }`,
        })}
        onDragEnter={() => {
          setHighlighted(true);
        }}
        onDragLeave={() => {
          setHighlighted(false);
        }}
      >
        <Typography sx={{ fontSize: 20, fontWeight: 700 }}>
          <input {...getInputProps()} />
          {gettext("Drag and drop files here or click")}
        </Typography>
        <CloudUploadIcon
          sx={{
            fontSize: 48,
          }}
        />
      </Box>
      <Collapse
        in={selectedFiles?.length > 0}
        sx={{
          p: 1,
          backgroundColor: "tertiary.0",
          border: "1px solid",
          minHeight: "52px",
          borderColor: "tertiary.100",
          borderRadius: "8px",
        }}
      >
        <aside>
          <Typography
            key={Math.random()}
            sx={{ fontSize: 20, fontWeight: 700 }}
          >
            {gettext("files")}
          </Typography>

          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{ flexWrap: "wrap" }}
          >
            {selectedFiles.map((file) => (
              <Chip
                key={Math.random()}
                label={file.path + " - (" + file.size + " bytes)"}
                variant="outlined"
                onDelete={handleDelete.bind(this, file)}
              />
            ))}
          </Stack>
        </aside>
      </Collapse>
      <Collapse in={loading}>
        {
          <Box
            sx={{
              width: "100%",
            }}
          >
            <LinearProgressWithLabel
              variant={progress ? "determinate" : "indeterminate"}
              value={progress}
            />
          </Box>
        }
      </Collapse>
    </>
  );
};

Dropzone.propTypes = {
  accept: object,
  getSelected: func,
  value: string,
};
